import React, {
    useState,
    useEffect,
    useRef,
    useCallback,
    useLayoutEffect,
    useMemo,
    ReactNode,
    FunctionComponent,
} from 'react';
import classnames from 'classnames';
import ArrowDown from 'dibs-icons/exports/ArrowDown';
import { useUID } from 'dibs-uid';
import { colors, SassyColorNames } from 'dibs-sassy/exports/colors';
import { Link } from 'dibs-elements/exports/Link';
import styles from './main.scss';
import { hexToRGBA } from './helpers';

const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect; // don't useLayoutEffect on server https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85#file-uselayouteffect-ssr-md

export type ExpandingAreaProps = {
    children?: ReactNode;
    dataTn?: string;
    expanded: boolean;
    expandingAreaClass?: string;
    label?: ReactNode;
    labelClass?: string;
    maxHeight?: string | number;
    onClick?: () => void;
    showArrow?: boolean;
    showOverflow?: boolean;
    wrapperClass?: string;
    collapsedHeight?: string | number;
    setIsContentExpandable?: (arg0: boolean) => void;
    gradientColor?: SassyColorNames;
};

const EXPANDED = 'expanded';
const PREP_COLLAPSE = 'prepCollapse';
const COLLAPSED = 'collapsed';
const COLLAPSING = 'collapsing';
const EXPANDING = 'expanding';

export const ExpandingArea: FunctionComponent<ExpandingAreaProps> = ({
    children,
    dataTn,
    expanded = false,
    expandingAreaClass,
    label,
    labelClass,
    maxHeight,
    onClick = () => {},
    showArrow,
    showOverflow,
    wrapperClass,
    collapsedHeight,
    setIsContentExpandable,
    gradientColor = 'sassyColorWhite',
}) => {
    const [expandedState, setExpandedState] = useState(expanded ? EXPANDED : COLLAPSED);
    const [localMaxHeight, setLocalMaxHeight] = useState(maxHeight || null);
    const [localContentExpandable, setLocalContentExpandable] = useState(false);
    const heightWrapper = useRef<HTMLDivElement | null>(null);
    if (
        typeof maxHeight === 'string' &&
        typeof collapsedHeight === 'string' &&
        parseInt(maxHeight) <= parseInt(collapsedHeight)
    ) {
        console.warn(
            "dibs-elements | Expanding Area | max height is less than collapsed height. this doesn't make any sense. don't do this."
        );
    }
    if (!colors[gradientColor]) {
        throw new Error(
            'dibs-elements | Expanding Area | This is not a dibs-sassy color! Check spelling, use camel case, look at the style guide, come back when you are ready.'
        );
    }

    const getNewMaxHeight = useCallback(() => {
        if (heightWrapper.current) {
            if (maxHeight) {
                let height = maxHeight;
                if (typeof maxHeight === 'string') {
                    height = parseInt(maxHeight);
                }
                return Math.min(heightWrapper.current.clientHeight, height as number);
            }
            return heightWrapper.current.clientHeight;
        }
        let height = collapsedHeight || 0;
        if (typeof collapsedHeight === 'string') {
            height = parseInt(collapsedHeight);
        }
        return height;
    }, [heightWrapper, collapsedHeight, maxHeight]);

    const updateHeight = useCallback(() => {
        const newHeight = getNewMaxHeight();
        if (localMaxHeight !== newHeight) {
            let newLocalMaxHeight = newHeight;
            if (typeof newHeight === 'string') {
                newLocalMaxHeight = parseInt(newHeight);
            }
            setLocalMaxHeight(newLocalMaxHeight as number);
        }
    }, [localMaxHeight, getNewMaxHeight]);

    useEffect(updateHeight);

    useEffect(() => {
        if (heightWrapper && heightWrapper.current) {
            let newCollapsedHeight = collapsedHeight || 0;
            if (typeof collapsedHeight === 'string') {
                newCollapsedHeight = parseInt(collapsedHeight);
            }
            const contentExpandable = heightWrapper.current.scrollHeight > newCollapsedHeight;
            setLocalContentExpandable(contentExpandable);
            if (setIsContentExpandable) {
                setIsContentExpandable(contentExpandable);
            }
        }
    }, [heightWrapper, collapsedHeight, setIsContentExpandable]);

    // useLayoutEffect ensures full transition to collapsed/expanded
    useIsomorphicLayoutEffect(() => {
        if (!expanded && expandedState === EXPANDED) {
            setExpandedState(PREP_COLLAPSE);
        }
        if (!expanded && expandedState === PREP_COLLAPSE) {
            setExpandedState(COLLAPSING);
        }
        if (expanded && expandedState === COLLAPSED) {
            setExpandedState(EXPANDING);
        }
        if (expanded && expandedState === COLLAPSING) {
            setExpandedState(EXPANDING);
        }
        if (!expanded && expandedState === EXPANDING) {
            setExpandedState(COLLAPSING);
        }
    }, [expanded, expandedState]);

    function handleTransitionEnd(): void {
        if (expanded && expandedState !== EXPANDED) {
            setExpandedState(EXPANDED);
        } else if (!expanded && expandedState !== COLLAPSED) {
            setExpandedState(COLLAPSED);
        }
    }
    const realCollapsedHeight = localContentExpandable ? collapsedHeight : 'auto';
    function getExpandingAreaHeight(): string | number | undefined {
        switch (expandedState) {
            case COLLAPSED:
                return collapsedHeight ? realCollapsedHeight : 0;
            case EXPANDING:
                return localMaxHeight || 'inherit';
            case EXPANDED:
                return (maxHeight && localMaxHeight) || 'auto';
            case PREP_COLLAPSE:
                return localMaxHeight || 'inherit';
            case COLLAPSING:
                return collapsedHeight ? realCollapsedHeight : 0;
        }
        return 0;
    }

    const expandingAreaHeight = getExpandingAreaHeight();
    const labelStyles = classnames(
        styles.labelWrapper,
        {
            [styles.stringLabel]: typeof label === 'string' || showArrow,
        },
        labelClass
    );

    const arrowStyles = classnames(styles.arrow, {
        [styles.arrowUp]: expanded,
    });

    const expandingAreaClassName = classnames(
        styles.expandingArea,
        {
            [styles.showOverflow]: showOverflow && expandedState === EXPANDED,
            // keep .noScroll class until it's fully expanded to prevent scrollbar from appearing
            [styles.noScroll]: expandedState !== EXPANDED,
        },
        expandingAreaClass
    );

    const gradientStyle = useMemo(() => {
        const color = colors[gradientColor];
        return {
            backgroundImage: `linear-gradient(${hexToRGBA(color, 0)} 0%, ${hexToRGBA(
                color,
                0.85
            )} 50%, ${hexToRGBA(color, 1)} 100%)`,
        };
    }, [gradientColor]);
    const uid = useUID();
    return (
        <div className={wrapperClass}>
            {label && (
                <Link
                    ariaControls={uid || undefined}
                    ariaExpanded={expandedState === EXPANDED}
                    underline="none"
                    className={labelStyles}
                    dataTn={dataTn}
                    onClick={onClick}
                >
                    <div className={styles.label}>{label}</div>
                    {(typeof label === 'string' || showArrow) && (
                        <ArrowDown className={arrowStyles} />
                    )}
                </Link>
            )}
            {children && (
                <div
                    id={uid || undefined}
                    data-tn={`${dataTn ? dataTn + '-' : ''}expanding-area`}
                    aria-expanded={expandedState === EXPANDED ? 'true' : 'false'}
                    className={expandingAreaClassName}
                    onTransitionEnd={handleTransitionEnd}
                    style={{
                        height: expandingAreaHeight,
                    }}
                >
                    <div
                        ref={heightWrapper}
                        data-tn={`${dataTn ? dataTn + '-' : ''}expanding-area-children-wrapper`}
                    >
                        {children}
                    </div>
                    {(expandedState === COLLAPSED || expandedState === COLLAPSING) &&
                        localContentExpandable && (
                            <div className={styles.gradient} style={gradientStyle} />
                        )}
                </div>
            )}
        </div>
    );
};
